import { zodResolver } from '@hookform/resolvers/zod';
import { IBrand, IClient } from '../../types/brand';
import { IAgent } from '../../types/agent';
import { Controller, useForm } from 'react-hook-form';
import { BrandSchema, BrandSchemaType } from '../../validations/brand';
import { addBrand, updateBrand } from '../../hooks/mutations';
import { useThemeStore } from '../../state/theme';
import { BtnPrimary } from '../common/Buttons';
import Select from 'react-select';
import { LoaderCircle } from 'lucide-react';
import { useResourceUpload } from '../../hooks/useResourceUpload';
import ResourceUploadSection from '../common/ResourceUploadSection';
import { useState } from 'react';
import LeavePoliciesEditor, {
  PolicyPeriod,
  toPolicyPeriods,
  fromPolicyPeriods,
} from './LeavePoliciesEditor';

interface AddBrandProps {
  initialData?: IClient;
  onSuccess: () => void;
  onError: (error: Error) => void;
  selectedBrand: IBrand;
  isEditing?: boolean;
  agentsData: IAgent[];
}

const AddBrand: React.FC<AddBrandProps> = ({
  initialData,
  onSuccess,
  onError,
  selectedBrand,
  isEditing,
  agentsData,
}) => {
  const { isDarkMode } = useThemeStore();

  const [kimPolicies, setKimPolicies] = useState<PolicyPeriod[]>(() =>
    toPolicyPeriods(selectedBrand?.configuration?.kimLeaves)
  );
  const [brandPolicies, setBrandPolicies] = useState<PolicyPeriod[]>(() =>
    toPolicyPeriods(selectedBrand?.configuration?.brandLeaves)
  );
  const [tolerancePeriod, setTolerancePeriod] = useState<number | ''>(
    selectedBrand?.configuration?.tolerancePeriod ?? 15
  );

  const resourceUpload = useResourceUpload({
    categories: ['brand', 'scenario'],
    existingResources: (selectedBrand?.resources ?? []).map((r) => ({
      ...r,
      uploadedAt: r.uploadedAt ?? new Date(),
    })),
  });

  const { mutate: updateBrandMutation, isPending: isUpdatingBrand } =
    updateBrand({
      onSuccess: async (data) => {
        try {
          const brand = data?.brand;
          if (brand?._id)
            await resourceUpload.uploadToServer(
              brand._id,
              (brand.resources ?? []).map((r) => ({
                ...r,
                uploadedAt: r.uploadedAt ?? new Date(),
              }))
            );
          onSuccess();
        } catch {
          onSuccess();
        }
      },
      onError,
    });

  const { mutate: addBrandMutation, isPending: isAddingBrand } = addBrand({
    onSuccess: async (data) => {
      try {
        const brand = data?.brand;
        if (brand?._id)
          await resourceUpload.uploadToServer(
            brand._id,
            (brand.resources ?? []).map((r) => ({
              ...r,
              uploadedAt: r.uploadedAt ?? new Date(),
            }))
          );
        onSuccess();
      } catch {
        onSuccess();
      }
    },
    onError,
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<Omit<BrandSchemaType, 'configuration'>>({
    resolver: zodResolver(BrandSchema.omit({ configuration: true })),
    defaultValues: {
      clientId: initialData?._id || '',
      name: selectedBrand?.name || '',
      websiteLink: selectedBrand?.websiteLink || '',
      onboardingDate: selectedBrand?.onboardingDate
        ? new Date(selectedBrand.onboardingDate).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      agents: selectedBrand?.agents || [],
    },
  });

  const inputClassName = `w-full rounded-md px-3 py-2 shadow-sm focus:outline-none ${
    isDarkMode
      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-indigo-500 focus:ring-indigo-500'
      : 'border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
  }`;

  const labelClassName = `block text-sm font-medium mb-1 ${
    isDarkMode ? 'text-gray-300' : 'text-gray-700'
  }`;

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      backgroundColor: isDarkMode ? '#374151' : 'white',
      borderColor: isDarkMode ? '#4B5563' : '#D1D5DB',
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: isDarkMode ? '#374151' : 'white',
    }),
    option: (
      base: any,
      state: { isFocused: boolean; isSelected: boolean }
    ) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#4F46E5'
        : state.isFocused
          ? isDarkMode
            ? '#4B5563'
            : '#F3F4F6'
          : 'transparent',
      color: state.isSelected ? 'white' : isDarkMode ? '#F3F4F6' : '#111827',
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: isDarkMode ? '#4B5563' : '#E5E7EB',
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: isDarkMode ? '#F3F4F6' : '#111827',
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      ':hover': { backgroundColor: isDarkMode ? '#6B7280' : '#D1D5DB' },
    }),
  };

  const onSubmit = (data: Omit<BrandSchemaType, 'configuration'>) => {
    const allResources = [
      ...resourceUpload.resources.brand,
      ...resourceUpload.resources.scenario,
    ];

    const brandData: IBrand = {
      ...data,
      resources: allResources,
      configuration: {
        tolerancePeriod: tolerancePeriod === '' ? 0 : tolerancePeriod,
        kimLeaves: fromPolicyPeriods(kimPolicies),
        brandLeaves: fromPolicyPeriods(brandPolicies),
      },
    };

    if (isEditing) {
      updateBrandMutation({ ...brandData, _id: selectedBrand._id });
    } else {
      addBrandMutation(brandData);
    }
  };

  const isLoading =
    resourceUpload.isUploading.brand ||
    resourceUpload.isUploading.scenario ||
    resourceUpload.isUploadingToServer;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <label htmlFor="clientId" className={labelClassName}>
          Client Name
        </label>
        <input
          type="text"
          id="clientId"
          value={initialData?.name}
          disabled
          className={inputClassName}
        />
      </div>

      <div>
        <label htmlFor="name" className={labelClassName}>
          Brand Name
        </label>
        <input
          type="text"
          id="name"
          {...register('name')}
          className={inputClassName}
        />
        {errors.name && (
          <span className="text-red-500 text-sm">{errors.name.message}</span>
        )}
      </div>

      <div>
        <label htmlFor="websiteLink" className={labelClassName}>
          Website Link
        </label>
        <input
          type="text"
          id="websiteLink"
          {...register('websiteLink')}
          className={inputClassName}
        />
        {errors.websiteLink && (
          <span className="text-red-500 text-sm">
            {errors.websiteLink.message}
          </span>
        )}
      </div>

      <div>
        <label htmlFor="onboardingDate" className={labelClassName}>
          Onboarding Date
        </label>
        <input
          type="date"
          id="onboardingDate"
          {...register('onboardingDate')}
          className={inputClassName}
        />
        {errors.onboardingDate && (
          <span className="text-red-500 text-sm">
            {errors.onboardingDate.message}
          </span>
        )}
      </div>

      {isEditing && (
        <div>
          <label htmlFor="agents" className={labelClassName}>
            Agents
          </label>
          <Controller
            name="agents"
            control={control}
            render={({ field }) => {
              const selectedOptions = agentsData
                .filter(
                  (agent) => agent.kimId && field.value?.includes(agent.kimId)
                )
                .map((agent) => ({ value: agent.kimId!, label: agent.name }));

              return (
                <Select
                  {...field}
                  isMulti
                  value={selectedOptions}
                  isSearchable={false}
                  menuIsOpen={false}
                  styles={selectStyles}
                  className="mt-1"
                  placeholder="No agents assigned"
                  onChange={(selected) =>
                    field.onChange(selected.map((o) => o.value))
                  }
                />
              );
            }}
          />
          {errors.agents && (
            <span className="text-red-500 text-sm">
              {errors.agents.message}
            </span>
          )}
        </div>
      )}

      <div>
        <label htmlFor="tolerancePeriod" className={labelClassName}>
          Tolerance Period (minutes)
        </label>
        <input
          type="number"
          id="tolerancePeriod"
          value={tolerancePeriod}
          onChange={(e) =>
            setTolerancePeriod(
              e.target.value === '' ? '' : Number(e.target.value)
            )
          }
          onWheel={(e) => (e.target as HTMLInputElement).blur()}
          min="0"
          step="any"
          className={inputClassName}
        />
        <p
          className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
        >
          Grace period for late clock-ins/outs
        </p>
      </div>

      {/* Leave Policies */}
      <div className="space-y-8">
        <LeavePoliciesEditor
          label="Kim Leave Policies"
          policies={kimPolicies}
          onChange={setKimPolicies}
          inputClassName={inputClassName}
          labelClassName={labelClassName}
          isDarkMode={isDarkMode}
        />
        <LeavePoliciesEditor
          label="Brand Leave Policies"
          policies={brandPolicies}
          onChange={setBrandPolicies}
          inputClassName={inputClassName}
          labelClassName={labelClassName}
          isDarkMode={isDarkMode}
        />
      </div>

      {/* Resources */}
      <div className="space-y-6">
        <ResourceUploadSection
          title="Upload Brand Documents"
          category="brand"
          resources={resourceUpload.resources.brand}
          onFilesChange={resourceUpload.handleNewFiles}
          onRemoveResource={resourceUpload.removeResource}
          onViewResource={resourceUpload.handleViewResource}
          isUploading={resourceUpload.isUploading.brand}
          accept=".pdf,.doc,.docx,.txt"
          maxSize={100}
          maxFiles={10}
        />
        <ResourceUploadSection
          title="Upload Scenario Documents"
          category="scenario"
          resources={resourceUpload.resources.scenario}
          onFilesChange={resourceUpload.handleNewFiles}
          onRemoveResource={resourceUpload.removeResource}
          onViewResource={resourceUpload.handleViewResource}
          isUploading={resourceUpload.isUploading.scenario}
          accept=".pdf,.doc,.docx,.txt"
          maxSize={100}
          maxFiles={10}
        />
      </div>

      <div className="flex justify-end">
        <BtnPrimary
          disabled={isAddingBrand || isUpdatingBrand || isLoading}
          type="submit"
        >
          {isAddingBrand ||
          isUpdatingBrand ||
          resourceUpload.isUploadingToServer ? (
            <div className="flex items-center gap-2">
              <span>
                {resourceUpload.isUploadingToServer
                  ? 'Uploading...'
                  : isUpdatingBrand
                    ? 'Updating'
                    : 'Adding'}
              </span>
              <LoaderCircle size={16} className="animate-spin" />
            </div>
          ) : isEditing ? (
            'Update Brand'
          ) : (
            'Add Brand'
          )}
        </BtnPrimary>
      </div>
    </form>
  );
};

export default AddBrand;
